import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore, useCartStore } from '../store/authStore';
import { useLanguage, languages } from '../context/LanguageContext';
import axios from 'axios';
import { FiShoppingCart, FiUser, FiMenu, FiX, FiHome, FiGrid, FiUsers, FiLogOut, FiCode, FiDollarSign, FiMap, FiSettings, FiBell, FiCheck, FiAward, FiMessageSquare, FiSend, FiFlag, FiTrendingUp, FiCalendar, FiHeadphones, FiList, FiBox, FiDownload, FiMonitor, FiSmartphone } from 'react-icons/fi';
import { MdTranslate } from 'react-icons/md';
import { RESOURCE_PACKS, initResourcePacks, subscribeResourcePacks, getResourcePackState } from '../utils/resourcePacks';
import { getDefaultAvatar } from '../utils/defaultAvatar';
import { LEVEL_BENEFITS, getFrameVisual } from '../utils/levelSystem';
import './Navbar.css';

const API = '/api';

export default function Navbar() {
  const logout = useAuthStore(s => s.logout);
  const getHeaders = useAuthStore(s => s.getHeaders);
  const user = useAuthStore(s => s.user);
  const isBanned = useAuthStore(s => s.isBanned);
  const banReason = useAuthStore(s => s.banReason);
  const checkBan = useAuthStore(s => s.checkBan);
  const { count, setCount } = useCartStore();
  const { t, language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [msgCount, setMsgCount] = useState(0);
  // 导航红点：签到 / 成就 / 活动，一次请求汇总
  const [badges, setBadges] = useState({ checkin: false, achievements: 0, activities: 0 });
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const notifCount = useAuthStore(s => s.notifCount);
  const fetchNotifCount = useAuthStore(s => s.fetchNotifCount);
  const [packBusy, setPackBusy] = useState(false);

  // 资源包在后台下载时，导航图标上显示进行中的标记（不登录也可用）。
  useEffect(() => {
    const refresh = () => setPackBusy(RESOURCE_PACKS.some(p => getResourcePackState(p.id).status === 'downloading'));
    initResourcePacks().then(refresh).catch(() => {});
    const un = subscribeResourcePacks(refresh);
    return un;
  }, []);

  const downloadLink = (
    <Link to="/downloads" className="notif-btn download-entry" title="资源包下载">
      <FiDownload />
      {packBusy && <span className="download-active-dot" />}
    </Link>
  );

  // 自己的头像框以服务器为准；本地缓存只作为旧数据和断网兜底。
  const getAvatarFrameId = (userId) => {
    if (String(userId) === String(user?.id) && user?.avatar_frame) return user.avatar_frame;
    try {
      const saved = localStorage.getItem(`frames_${userId}`);
      if (saved) {
        const settings = JSON.parse(saved);
        if (settings.avatarFrame) return settings.avatarFrame;
      }
    } catch(e) {}
    return 'frame_default';
  };

  // 读取用户头像框样式
  const getAvatarFrameStyle = (userId) => {
    const frameId = (String(userId) === String(user?.id) && user?.avatar_frame)
      ? user.avatar_frame
      : getAvatarFrameId(userId);
    try {
      const allFrames = Object.values(LEVEL_BENEFITS.avatarFrames);
      const frameObj = allFrames.find(f => f.id === frameId);
      if (frameObj) return getFrameVisual(frameId);
    } catch(e) {}
    return {};
  };

  useEffect(() => {
    if (!user) return undefined;
    const headers = getHeaders();
    const refreshCounts = () => {
      fetchNotifCount(headers);
      axios.get(`${API}/messages/unread-count`, { headers })
        .then(res => setMsgCount(res.data.count)).catch(() => {});
      // 导航红点：签到 / 成就 / 活动
      axios.get(`${API}/daily/badges`, { headers })
        .then(res => setBadges({
          checkin: !!res.data?.checkin,
          achievements: Number(res.data?.achievements) || 0,
          activities: Number(res.data?.activities) || 0,
        }))
        .catch(() => {});
    };
    axios.get(`${API}/games/user/cart`, { headers })
      .then(res => setCount(res.data.length)).catch(() => {});
    refreshCounts();
    // 检查封禁状态
    checkBan();
    const timer = setInterval(refreshCounts, 20000);
    // 页面里领完奖励后立刻刷新红点，不用等 20 秒或刷新网页
    window.addEventListener('iw:badges-changed', refreshCounts);
    return () => {
      clearInterval(timer);
      window.removeEventListener('iw:badges-changed', refreshCounts);
    };
  }, [user, location.pathname]);

  const navLinks = [
    { to: '/', label: t('navHome'), icon: <FiHome /> },
    { to: '/store', label: t('store'), icon: <FiGrid /> },
    { to: '/community', label: t('community'), icon: <FiUsers /> },
    { to: '/social', label: t('social'), icon: <FiUsers /> },
    { to: '/maps', label: t('mapWorkshop'), icon: <FiMap /> },
  ];

  const adminLink = (user?.isAdmin) ? { to: '/admin', label: t('adminPanel'), icon: <FiSettings /> } : null;
  const hasAnyBadge = badges.checkin || badges.achievements > 0 || badges.activities > 0;
  const currentLang = languages.find(l => l.code === language) || languages[0];
  const openOnboarding = (event) => {
    if (event) event.stopPropagation();
    setUserMenuOpen(false);
    setMobileOpen(false);
    window.dispatchEvent(new Event('iw:onboarding-open'));
  };

  return (
    <>
      {isBanned && (
        <div style={{
          background:'linear-gradient(135deg,#e74c3c,#c0392b)', color:'#fff',
          textAlign:'center', padding:'8px 16px', fontSize:13, fontWeight:500,
          borderBottom:'1px solid rgba(255,255,255,0.1)'
        }}>
        你的账号已被封禁 - {banReason}。
          <span style={{marginLeft:12,textDecoration:'underline',cursor:'pointer'}}
            onClick={() => { logoutFn(); localStorage.clear(); window.location.href='/login'; }}>
            点击退出登录
          </span>
        </div>
      )}
      <nav className="navbar">
      <div className="navbar-inner">
        {/* Logo */}
        <Link to="/" className="navbar-logo">
          <img src="/logo.png" alt="无限世界" style={{ width: 36, height: 36, objectFit: 'contain' }} />
        </Link>

        <div className="navbar-links">
          {navLinks.map(l => (
            <Link key={l.to} to={l.to} className={`nav-link ${location.pathname === l.to ? 'active' : ''}`}>
              {l.icon} <span>{l.label}</span>
            </Link>
          ))}
          {adminLink && (
            <Link to={adminLink.to} className={`nav-link admin ${location.pathname === adminLink.to ? 'active' : ''}`}>
              {adminLink.icon} <span>{adminLink.label}</span>
            </Link>
          )}
        </div>

        <div className="navbar-actions">
          {/* 语言切换 */}
          <div className="lang-menu-wrap">
            <button
              className="lang-btn"
              onClick={() => setLangMenuOpen(!langMenuOpen)}
              title={t('language')}
              aria-label={`${t('language')}：${currentLang.name}`}
              aria-haspopup="menu"
              aria-expanded={langMenuOpen}
            >
              <MdTranslate className="lang-btn-icon" aria-hidden="true" />
              <span className="lang-btn-label">{currentLang.name}</span>
              <span className="lang-caret" aria-hidden="true" />
            </button>
            {langMenuOpen && (
              <div className="lang-dropdown" role="menu">
                {languages.map(l => (
                  <button 
                    key={l.code} 
                    className={`lang-option ${l.code === language ? 'active' : ''}`}
                    role="menuitemradio"
                    aria-checked={l.code === language}
                    onClick={() => { changeLanguage(l.code); setLangMenuOpen(false); }}
                  >
                    <span className="lang-option-name">{l.name}</span>
                    {l.code === language && <FiCheck className="lang-option-check" aria-hidden="true" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {user ? (
            <>
              {/* 消息 */}
              <Link to="/messages" className="notif-btn" title="消息">
                <FiMessageSquare />
                {msgCount > 0 && <span className="notif-badge">{msgCount > 99 ? '99+' : msgCount}</span>}
              </Link>

              {/* 游戏库 */}
              <Link to="/library" className="notif-btn" title={t('library')}>
                <FiBox />
              </Link>

              {/* 通知 */}
              <Link to="/notifications" className="notif-btn" title="通知">
                <FiBell />
                {notifCount > 0 && <span className="notif-badge">{notifCount > 99 ? '99+' : notifCount}</span>}
              </Link>

              {/* 购物车 */}
              <Link to="/cart" className="cart-btn">
                <FiShoppingCart />
                {count > 0 && <span className="cart-badge">{count}</span>}
              </Link>

              {/* 资源包下载（本机存储，无需登录） */}
              {downloadLink}

              

              <div className="user-menu-wrap" onClick={() => setUserMenuOpen(!userMenuOpen)}>
                {/* 头像和它的角标包一层：红点靠 avatar-slot 定位，
                    不再靠手算 left:30px，头像尺寸变了也不会歪 */}
                <div className="user-avatar-slot">
                  <div className={`avatar-frame frame-${getAvatarFrameId(user.id)}`} style={getAvatarFrameStyle(user.id)}>
                    <img src={user.avatar || '/avatar.png'} alt="" className="user-avatar img-fade-in" onLoad={e => e.target.classList.add('img-loaded')} onError={e => { e.target.src = getDefaultAvatar(user.username); e.target.classList.add('img-loaded'); }} />
                  </div>
                  {hasAnyBadge && <span className="menu-dot menu-dot-avatar" title="有可领取的内容" />}
                </div>
                <span className="user-name">{user.username}</span>
                {userMenuOpen && (
                  <div className="user-dropdown">
                    <Link to={`/profile/${user.id}`} state={{ fromNavbar: true }}><FiUser /> {t('profile')}</Link>
                    <Link to="/daily">
                      <FiCalendar /> 每日签到
                      {badges.checkin && <span className="menu-dot" title="今天还没签到" />}
                    </Link>
                    <Link to="/achievements">
                      <FiAward /> 成就中心
                      {badges.achievements > 0 && <span className="menu-dot" title={`${badges.achievements} 个成就可领取`} />}
                    </Link>
                    <Link to="/level-benefits" className="level-benefits-link">
                      <FiAward /> 等级中心
                    </Link>
                    <Link to="/activities">
                      <FiBox /> 活动
                      {badges.activities > 0 && (
                        <span className="menu-dot" title={`${badges.activities} 个活动奖励可领取`} />
                      )}
                    </Link>
                    <button type="button" onClick={openOnboarding}><FiSmartphone /> 新手引导</button>
                    <Link to="/wallet"><FiDollarSign /> {t('myWallet')}</Link>
                    <Link to="/creator"><FiCode /> {t('creatorCenter')}</Link>
                    <Link to="/login-records"><FiList /> {t('loginRecords')}</Link>
                    <Link to="/settings"><FiSettings /> {t('settings')}</Link>
                    <Link to="/customer-service"><FiHeadphones /> 帮助</Link>
                    <div className="dropdown-divider"></div>
                    <button onClick={() => { logout(); navigate('/'); }}><FiLogOut /> {t('logout')}</button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="auth-buttons">
              {downloadLink}
              <Link to="/login" className="nav-login-link">{t('login')}</Link>
              <Link to="/register">{t('register')}</Link>
            </div>
          )}
        </div>

        <button className="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>

      {mobileOpen && (
        <div className="mobile-menu">
          {/* 语言切换 */}
          <div className="mobile-lang-section">
            <div className="mobile-lang-head">
              <span className="mobile-lang-title"><MdTranslate aria-hidden="true" /> {t('language')}</span>
              <span className="mobile-lang-current">{currentLang.name}</span>
            </div>
            <div className="mobile-lang-list">
              {languages.map(l => (
                <button 
                  key={l.code} 
                  className={`mobile-lang-btn ${l.code === language ? 'active' : ''}`}
                  onClick={() => { changeLanguage(l.code); setMobileOpen(false); }}
                >
                  {l.name}
                </button>
              ))}
            </div>
          </div>
          {navLinks.map(l => (
            <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}>{l.icon} {l.label}</Link>
          ))}
          <Link to="/downloads" onClick={() => setMobileOpen(false)}><FiDownload /> 资源包下载</Link>
          {user && <Link to="/cart" onClick={() => setMobileOpen(false)}><FiShoppingCart /> 购物车</Link>}
          {adminLink && (
            <Link to={adminLink.to} onClick={() => setMobileOpen(false)}>{adminLink.icon} {adminLink.label}</Link>
          )}
          {user ? (
            <>
              <div className="mobile-divider"></div>
              <Link to="/messages" onClick={() => setMobileOpen(false)}><FiMessageSquare /> {t('messages')} {msgCount > 0 && `(${msgCount})`}</Link>
              <Link to="/notifications" onClick={() => setMobileOpen(false)}><FiBell /> {t('notifications')} {notifCount > 0 && `(${notifCount})`}</Link>
              <Link to={{ pathname: `/profile/${user.id}`, state: { fromNavbar: true } }} onClick={() => setMobileOpen(false)}><FiUser /> {t('profile')}</Link>
              <Link to="/library" onClick={() => setMobileOpen(false)}><FiBox /> {t('library')}</Link>
              <Link to="/daily" onClick={() => setMobileOpen(false)}>
                <FiCalendar /> 每日签到
                {badges.checkin && <span className="menu-dot" />}
              </Link>
              <Link to="/achievements" onClick={() => setMobileOpen(false)}>
                <FiAward /> 成就中心
                {badges.achievements > 0 && <span className="menu-dot" />}
              </Link>
              <Link to="/activities" onClick={() => setMobileOpen(false)}>
                <FiBox /> 活动
                {(badges.activities > 0) && <span className="menu-dot" />}
              </Link>
              <button type="button" onClick={openOnboarding}><FiSmartphone /> 新手引导</button>
              <Link to="/wallet" onClick={() => setMobileOpen(false)}><FiDollarSign /> {t('myWallet')}</Link>
              <Link to="/creator" onClick={() => setMobileOpen(false)}><FiCode /> {t('creatorCenter')}</Link>
              <Link to="/login-records" onClick={() => setMobileOpen(false)}><FiMonitor /> {t('loginRecords')}</Link>
              <Link to="/settings" onClick={() => setMobileOpen(false)}><FiSettings /> {t('settings')}</Link>
              <button onClick={() => { logout(); setMobileOpen(false); navigate('/'); }}><FiLogOut /> {t('logout')}</button>
            </>
          ) : (
            <>
              <div className="mobile-divider"></div>
              <Link to="/login" onClick={() => setMobileOpen(false)} className="nav-login-link">{t('login')}</Link>
              <Link to="/register" onClick={() => setMobileOpen(false)}>{t('register')}</Link>
            </>
          )}
        </div>
      )}
    </nav>
    </>
  );
}
